import React, { useState, useEffect, useRef } from "react";
import { ECharts } from "echarts";
import * as echarts from "echarts";
import DeviceMetricApi from "../../../../../../../api/DeviceMetricApi";
import StringUtils from "../../../../../../../util/StringUtils";
import SeriesColorUtils from "../../../../../../../util/SeriesColorUtils";
import {DeviceMetricsChartsWrapper, PropertyItem, PropertyName, PropertyValue, PropertyChart } from "./DeviceMetricsCharts.styled";

interface DeviceMetricsChartsProps {
    deviceId: string;
}

export const DeviceMetricsCharts: React.FC<DeviceMetricsChartsProps> = (props)=> {
    const { deviceId } = props;

    // api
    let deviceMetricApi = new DeviceMetricApi();

    let echartsDomsRef = useRef<HTMLElement[]>([]);
    let echartsRef = useRef<ECharts[]>([]);
    let deviceMetricsSeriesListRef = useRef([]);

    let [deviceMetricProperties, setDeviceMetricProperties] = useState([]);
    let [loading, setLoading] = useState(true);

    let fetchRealtimeMetricsSeries = async ()=> {
        let deviceMetricsSeriesList = (await deviceMetricApi.getRealtimeDeviceMetricsSeries(deviceId)).data.data;
        deviceMetricsSeriesListRef.current = deviceMetricsSeriesList;
        
        if (deviceMetricProperties.length <= 0) {
            let properties = [];
            for(let deviceMetricsSeries of deviceMetricsSeriesList) {
                properties.push(deviceMetricsSeries.property);
            }
            setDeviceMetricProperties(properties);
        }
        
        setLoading(false);
        return deviceMetricsSeriesList;
    }

    let updateSeriesData = async (deviceMetricsSeriesList: any[])=> {
        for(let i=0; i<deviceMetricsSeriesList.length; i++) {
            let deviceMetricsSeries = deviceMetricsSeriesList[i];
            
            let seriesData = [];
            let metrics = deviceMetricsSeries.metrics;
            let lastMetricTimestamp: number = null;
            let paddingGapMillis = 5 * 10000;
            for (let i = 0; i < metrics.length; i++) {
                let metric = metrics[i];
                
                if (lastMetricTimestamp !=null && Math.abs(metric.timestamp - lastMetricTimestamp) > paddingGapMillis) {
                    let downDataPoint = {name: '' + (Number(lastMetricTimestamp)+1), value: [(Number(lastMetricTimestamp)+1), 0]};
                    seriesData.push(downDataPoint);

                    let upDataPoint: any = {name: '' + (Number(metric.timestamp)-1), value: [(Number(metric.timestamp)-1), 0]};
                    seriesData.push(upDataPoint);
                }
                lastMetricTimestamp = metric.timestamp;

                // add to series data
                seriesData.push({
                    name: metric.timestamp + '',
                    value: [Number(metric.timestamp), Number(metric.value)],
                });
            }

            // echarts options 
            if (echartsRef.current[i] != null) {
                echartsRef.current[i].setOption({
                    series: [{
                        data: seriesData
                    }]
                });
            }
        }
    };

    let formatPropertyValue = (metric, property) => {
        if (property.type === 'FLOAT32') {
            return `${parseFloat(metric.value).toFixed(property.scale)}${StringUtils.nonull(property.unit)}`;
        }
        return `${metric.value}${StringUtils.nonull(property.unit)}`;
    };

    let initECharts = (dom: HTMLElement, index: number)=> {
        if (dom == null) {
            return;
        }
        if (echarts.getInstanceByDom(dom)) {
            return;
        }

        echartsDomsRef.current.push(dom);
        let seriesIndex = echartsDomsRef.current.indexOf(dom);

        // init echarts
        let chart = echarts.init(dom);
        echartsRef.current.push(chart);

        let property = deviceMetricProperties[seriesIndex];

        let option = {
            backgroundColor: 'transparent',
            grid: {
                x: 2, y: 2, x2: 2, y2: 2, 
            },
            xAxis: {
                type: 'time',
                splitLine: { show: false },
                axisLine: { show: false },
                axisTick: { show: false }
            },
            yAxis: {
                type: 'value',
                boundaryGap: [0, '100%'],
                splitLine: {
                    show: false,
                    lineStyle: {
                        color: '#CCCCCC',
                        width: 1,
                        type: 'dashed',
                    }
                }
            },
            series: [
                {
                    name: property?.name || '',
                    type: 'line',
                    color: SeriesColorUtils.getSeriesColor(seriesIndex),
                    smooth: false,
                    showSymbol: false,
                    lineStyle: {
                        width: 1,
                        opacity: 0.3,
                    },
                    areaStyle: {
                        opacity: 0.2,
                    },
                    data: [],
                    shadowBlur: 20,
                }
            ],
        };

        chart.setOption(option);
        
        chart.group = 'DeviceMetricsCharts';
        echarts.connect('DeviceMetricsCharts');

        if (deviceMetricProperties.length == seriesIndex + 1) {
            setTimeout(()=> {
                updateSeriesData(deviceMetricsSeriesListRef.current);
            });
        }
    }

    useEffect(()=> {
        let refreshTaskId: any = null;
        let disposed = false;

        let init = async ()=> {
            // fetch data
            let deviceMetricsSeriesList = await fetchRealtimeMetricsSeries();
            deviceMetricsSeriesListRef.current = deviceMetricsSeriesList;

            refreshTaskId = window.setInterval(async ()=> {
                let deviceMetricsSeriesList = await fetchRealtimeMetricsSeries();
                deviceMetricsSeriesListRef.current = deviceMetricsSeriesList;
                updateSeriesData(deviceMetricsSeriesList);

                if (disposed) {
                    window.clearInterval(refreshTaskId);
                }
            }, 5000);
        };

        init();

        let resizeListener = ()=> {
            for(let chart of echartsRef.current) {
                chart.resize();
            }
        };
        window.addEventListener('resize', resizeListener);

        
        return ()=> {
            disposed = true;
            for(let chart of echartsRef.current) {
                chart.dispose();
            }

            // clear interval
            if (refreshTaskId!=null) {
                window.clearInterval(refreshTaskId);
            }

            // clear resize listener
            window.removeEventListener('resize', resizeListener);
        }
    }, []);

    let getLatestMetricValue = (index: number)=> {
        let deviceMetricsSeries = deviceMetricsSeriesListRef.current[index];
        if (deviceMetricsSeries && deviceMetricsSeries.metrics && deviceMetricsSeries.metrics.length > 0) {
            let latestMetrics = deviceMetricsSeries.metrics[deviceMetricsSeries.metrics.length-1];
            let property = deviceMetricProperties[index];
            return formatPropertyValue(latestMetrics, property);
        }
        return '';
    };


    return <DeviceMetricsChartsWrapper>
            {loading ? '...' : deviceMetricProperties.map((property, index) => {
                return (
                    <PropertyItem key={`${property.id}-${index}`}>
                        <PropertyChart ref={(dom) => initECharts(dom as HTMLElement, index)} />
                        <PropertyName>{property.name}</PropertyName>
                        <PropertyValue>
                            {getLatestMetricValue(index)}
                        </PropertyValue>
                    </PropertyItem>
                );
            })}
        </DeviceMetricsChartsWrapper>
};

export default DeviceMetricsCharts;